import { ArrowLeft, Play, RotateCcw } from "lucide-react";
import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ConsistentTooltip } from "@/components/ui/ConsistentTooltip";
import { isElectron } from "@/services/storage/LocalFileSystemClient";
import { useEditorStore } from "@/store/editorStore";

interface EditorHeaderProps {
  onExport?: () => void;
  onRefresh?: () => void;
  centerBottomContent?: ReactNode;
}

export const EditorHeader = ({
  onExport,
  onRefresh: _onRefresh,
  centerBottomContent,
}: EditorHeaderProps) => {
  const location = useLocation();

  // Get project and preview mode from store
  const {
    project,
    isPreviewMode,
    setIsPreviewMode,
    currentRoute,
    panoramas,
    selectedPanorama,
    setSelectedPanorama,
  } = useEditorStore();

  if (!project) {
    return null;
  }

  // Handler for Preview Start button - goes to first panorama
  const handlePreviewStart = () => {
    if (panoramas.length === 0) return;

    const firstPanorama = panoramas[0];
    if (firstPanorama) {
      setSelectedPanorama(firstPanorama);
      setIsPreviewMode(true);
    }
  };

  // Handler for Preview Current button - keeps current panorama
  const _handlePreviewCurrent = () => {
    if (!selectedPanorama && panoramas.length > 0) {
      setSelectedPanorama(panoramas[0]);
    }
    setIsPreviewMode(true);
  };

  return (
    <header className={`bg-gray-800 border-b border-gray-700 px-4 py-3 flex items-center justify-between w-full relative z-40 ${isElectron() ? "electron-drag" : ""}`}>
      <div
        className={`flex items-center space-x-4 ${isElectron() ? "pl-20 electron-no-drag" : ""}`}
      >
        {isPreviewMode && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white"
            onClick={handlePreviewStart}
            disabled={panoramas.length === 0}
          >
            <RotateCcw size={14} className="mr-1" />
            Preview from start
          </Button>
        )}
        {!isPreviewMode && (
          <Link to="/dashboard">
            <Button
              variant="ghost"
              size="sm"
              className="text-gray-300 hover:text-white hover:bg-gray-700"
            >
              <ArrowLeft size={16} className="mr-2" />
              Dashboard
            </Button>
          </Link>
        )}
      </div>

      <div className={`flex items-center space-x-2 ${isElectron() ? "electron-no-drag" : ""}`}>
        {centerBottomContent ? <div className="mr-1">{centerBottomContent}</div> : null}
        {!location.pathname.includes("/maps") && (
          <ConsistentTooltip
            content={isPreviewMode ? "Exit Preview Mode" : "Preview Mode"}
            trigger={
              <button
                type="button"
                onClick={() => setIsPreviewMode(!isPreviewMode)}
                className={`inline-flex items-center justify-center h-9 w-9 rounded-md transition-colors outline-none border-0 ${
                  isPreviewMode
                    ? "bg-blue-600 text-white hover:bg-blue-500"
                    : "text-gray-300 hover:text-white hover:bg-gray-700"
                }`}
              >
                <Play size={14} className={isPreviewMode ? "fill-white" : ""} />
              </button>
            }
          />
        )}
        {onExport &&
          (currentRoute === "editor" ||
            currentRoute === "skin" ||
            currentRoute === "variables" ||
            currentRoute === "settings") && (
            <ConsistentTooltip
              content="Publish as standalone tour"
              trigger={
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  className="text-gray-200 border-gray-600 bg-gray-600 hover:bg-gray-500 hover:text-white"
                  onClick={onExport}
                >
                  <ArrowLeft size={16} className="rotate-90" />
                  Publish
                </Button>
              }
            ></ConsistentTooltip>
          )}
      </div>
    </header>
  );
};
